import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { toast } from 'sonner';
import { useAuth } from '../hooks/useAuth';
import { useSidePanel } from './SidePanelContext';
import {
  ClinicAgentSocket,
  ClinicMessagePayload,
  ClinicSocketState,
} from '../services/clinicAgentSocket';
import { agentConversationsService } from '../services/agentConversationsService';
import { proposalErrorMessage, resolveProposals } from '../services/clinicAgentProposals';
import { patientsService } from '../services/patientsService';
import { isAudioFileType } from '../utils/audio';
import { emitPatientContextChanged } from '../services/patientAgentContextEvents';
import {
  ChatMessage,
  Proposal,
  ProposalResolveResult,
  ProposalState,
  ProposalStatus,
} from '../models/clinicAgent';
import { Conversation, ConversationListFilter } from '../models/agentConversation';

// The clinician's conversations with the clinic agent (contract v3.1). One
// socket; every turn frame carries the conversation it belongs to, so the
// streaming state (provisional reply, status chip, stall timer) is kept per
// conversation and several conversations can be answering at once.
//
// Mounted by ClinicAgentRoot only when the backend has conversations; the
// single-thread ClinicAgentContext stays as the fallback until then.

export type DraftPatient = { id: number; name: string };

// What the panel shows: a saved conversation, or a draft that only exists in
// the browser until its first message creates it on the server.
export type ActiveTarget =
  | { kind: 'conversation'; id: number }
  | { kind: 'draft'; key: string; patient: DraftPatient | null };

export type PanelView = 'list' | 'thread';

export interface Transcript {
  messages: ChatMessage[];
  loaded: boolean;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  nextBeforeId: number | null;
  typing: boolean;
  statusLabel: string | null;
  error: boolean;
}

type AgentConversationsValue = {
  connectionState: ClinicSocketState;
  authError: boolean;

  // List (main view of the panel).
  conversations: Conversation[];
  listFilter: ConversationListFilter;
  listQuery: string;
  listLoading: boolean;
  listError: boolean;
  hasMoreConversations: boolean;
  setListFilter: (f: ConversationListFilter) => void;
  setListQuery: (q: string) => void;
  loadMoreConversations: () => Promise<void>;
  reloadConversations: () => void;
  // Conversations of the patient whose page is open (group at the top).
  routePatient: DraftPatient | null;
  routePatientConversations: Conversation[];
  unread: Set<number>;

  // What is on screen.
  view: PanelView;
  active: ActiveTarget | null;
  activeConversation: Conversation | null;
  activeTranscript: Transcript;
  activeBusy: boolean;
  pinned: boolean;
  togglePin: () => void;
  showList: () => void;
  openConversation: (id: number) => void;
  startDraft: (patient?: DraftPatient | null) => void;
  setDraftPatient: (patient: DraftPatient | null) => void;
  // "Preguntar a Sissi": the patient's newest conversation, or a draft for them.
  openForPatient: (patient: DraftPatient) => Promise<void>;
  syncRoutePatient: (patientId?: number) => void;

  sendMessage: (text: string, file?: { dataUrl: string; type: string; name: string; durationMillis?: number }) => void;
  retrySend: (messageId: string) => void;
  loadOlderMessages: () => Promise<void>;

  rename: (id: number, title: string) => Promise<void>;
  setArchived: (id: number, archived: boolean) => Promise<Conversation>;
  remove: (id: number) => Promise<void>;

  resolveProposal: (proposalId: string, decision: 'accept' | 'reject') => Promise<void>;
  resolveManyProposals: (proposalIds: string[], decision: 'accept' | 'reject') => Promise<void>;
};

const AgentConversationsContext = createContext<AgentConversationsValue | undefined>(undefined);

const ACTIVE_KEY = 'clinic-agent-conversation';
const PAGE_SIZE = 20;
const FIRST_MESSAGES = 50;
const OLDER_MESSAGES = 30;
// Transcripts kept in memory besides the one on screen.
const TRANSCRIPT_CACHE = 10;
// Same safety net as the single-thread chat: above the backend's 300 s read timeout.
export const STREAM_STALL_TIMEOUT_MS = 330_000;
const RUN_FAILED_NOTICE = 'La respuesta no se completó. Reenvía tu mensaje.';
// The backend answers a message at once (typing, conversation_created or
// error). Silence this long means it dropped the frame (e.g. an attachment
// with no text), so the turn is ended and the message offered for retry.
export const SEND_ACK_TIMEOUT_MS = 20_000;
const NO_ACK_NOTICE = 'El servidor no respondió a este mensaje. Reenvíalo; si lleva un archivo, añade también un texto.';
const CONNECTION_LOST_NOTICE =
  'Se perdió la conexión y la respuesta no se completó. Reenvía tu mensaje cuando se restablezca la conexión.';

export const EMPTY_TRANSCRIPT: Transcript = {
  messages: [],
  loaded: false,
  loading: false,
  loadingMore: false,
  hasMore: false,
  nextBeforeId: null,
  typing: false,
  statusLabel: null,
  error: false,
};

const SEND_ERROR_TEXT: Record<string, string> = {
  patient_not_found: 'No tienes acceso a este paciente. El mensaje no se envió.',
  conversation_not_found: 'Esta conversación ya no existe. El mensaje no se envió.',
  patient_mismatch: 'No se pudo enviar el mensaje en esta conversación. Recarga la página.',
  audio_invalid: 'No se pudo enviar la nota de voz: audio no admitido o demasiado grande. Grábala de nuevo.',
};

let idSeq = 0;
function makeId(prefix = 'msg'): string {
  idSeq += 1;
  return `${prefix}-${Date.now()}-${idSeq}`;
}

const convKey = (id: number) => `c:${id}`;

function readStoredActive(): number | null {
  try {
    const raw = localStorage.getItem(ACTIVE_KEY);
    const id = raw ? Number(raw) : NaN;
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
}

function storeActive(id: number | null) {
  try {
    if (id == null) localStorage.removeItem(ACTIVE_KEY);
    else localStorage.setItem(ACTIVE_KEY, String(id));
  } catch {
    /* storage blocked: the panel just opens on the list next time */
  }
}

// Newest first, as the backend orders the list.
export function sortConversations(list: Conversation[]): Conversation[] {
  return [...list].sort((a, b) => {
    const ta = a.lastMessageAt ? Date.parse(a.lastMessageAt) : 0;
    const tb = b.lastMessageAt ? Date.parse(b.lastMessageAt) : 0;
    return tb - ta || b.id - a.id;
  });
}

function matchesFilter(c: Conversation, filter: ConversationListFilter): boolean {
  if (filter === 'archived') return c.archived;
  if (c.archived) return false;
  return filter === 'general' ? c.kind === 'general' : true;
}

function terminalStatus(status: string): 'applied' | 'discarded' | null {
  const s = (status || '').toLowerCase();
  if (['discarded', 'rejected', 'declined', 'cancelled', 'canceled'].includes(s)) return 'discarded';
  if (['applied', 'accepted', 'done', 'success', 'ok'].includes(s)) return 'applied';
  return null;
}

function patchProposals(
  messages: ChatMessage[],
  proposalId: string,
  patch: Partial<ProposalState>,
): ChatMessage[] {
  let changedAny = false;
  const next = messages.map((m) => {
    if (!m.proposals) return m;
    let changed = false;
    const proposals = m.proposals.map((p) => {
      if (p.id !== proposalId) return p;
      changed = true;
      return { ...p, ...patch };
    });
    if (!changed) return m;
    changedAny = true;
    return { ...m, proposals };
  });
  return changedAny ? next : messages;
}

// `enabled` false: renders the same tree but stays inert (no socket, no
// requests, no context), so ClinicAgentRoot can switch modes without
// remounting the app shell.
export const AgentConversationsProvider = ({ children, enabled = true }: { children: ReactNode; enabled?: boolean }) => {
  const { access: token, refreshTokens } = useAuth();
  const access = enabled ? token : null;
  const sidePanel = useSidePanel();
  const panelOpen = !!sidePanel?.agentOpen;

  const [connectionState, setConnectionState] = useState<ClinicSocketState>('closed');
  const [authError, setAuthError] = useState(false);

  // Every conversation the client knows about, by id; the lists hold ids.
  const [byId, setById] = useState<Record<number, Conversation>>({});
  const [listIds, setListIds] = useState<number[]>([]);
  const [listFilter, setListFilterState] = useState<ConversationListFilter>('all');
  const [listQuery, setListQueryState] = useState('');
  const [listPage, setListPage] = useState(1);
  const [listTotalPages, setListTotalPages] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(false);
  const [routePatient, setRoutePatient] = useState<DraftPatient | null>(null);
  const [routeIds, setRouteIds] = useState<number[]>([]);
  const [unread, setUnread] = useState<Set<number>>(new Set());

  const [transcripts, setTranscripts] = useState<Record<string, Transcript>>({});
  const [active, setActiveState] = useState<ActiveTarget | null>(null);
  const [view, setView] = useState<PanelView>('list');
  const [pinned, setPinned] = useState(false);

  const activeRef = useRef<ActiveTarget | null>(null);
  activeRef.current = active;
  const viewRef = useRef<PanelView>(view);
  viewRef.current = view;
  const panelOpenRef = useRef(panelOpen);
  panelOpenRef.current = panelOpen;
  const pinnedRef = useRef(pinned);
  pinnedRef.current = pinned;
  const byIdRef = useRef(byId);
  byIdRef.current = byId;
  const transcriptsRef = useRef(transcripts);
  transcriptsRef.current = transcripts;
  const listFilterRef = useRef(listFilter);
  listFilterRef.current = listFilter;
  const listQueryRef = useRef(listQuery);
  listQueryRef.current = listQuery;
  const routePatientRef = useRef<DraftPatient | null>(null);
  routePatientRef.current = routePatient;

  const socketRef = useRef<ClinicAgentSocket | null>(null);
  const refreshTokensRef = useRef(refreshTokens);
  useEffect(() => {
    refreshTokensRef.current = refreshTokens;
  }, [refreshTokens]);

  // Per-run streaming bookkeeping. Run ids are unique across conversations.
  const completedRunIdsRef = useRef<Set<number>>(new Set());
  const provisionalRef = useRef<Map<number, { key: string; messageId: string; call: number }>>(new Map());
  const stallTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Transcript keys whose reply is still expected.
  const awaitingRef = useRef<Set<string>>(new Set());
  // First message of a draft, by client_message_id, until conversation_created.
  const draftSendsRef = useRef<Map<string, string>>(new Map());
  // Exact frames of failed sends, by bubble id, so a retry resends them.
  const failedPayloadsRef = useRef<Map<string, { key: string; payload: ClinicMessagePayload }>>(new Map());
  // Frames sent and not yet answered, by bubble id, in case the server rejects them.
  const sentPayloadsRef = useRef<Map<string, { key: string; payload: ClinicMessagePayload }>>(new Map());
  // Sent messages not yet answered by the server, by bubble id.
  const ackTimersRef = useRef<Map<string, { key: string; timer: ReturnType<typeof setTimeout> }>>(new Map());
  // Recently opened transcript keys, most recent last (cache eviction order).
  const openedOrderRef = useRef<string[]>([]);
  const patientNameCacheRef = useRef<Map<number, string>>(new Map());
  const routeSeqRef = useRef(0);
  const listSeqRef = useRef(0);
  const wasDisconnectedRef = useRef(false);

  // ---------------------------------------------------------------- helpers

  const updateTranscript = useCallback((key: string, fn: (t: Transcript) => Transcript) => {
    setTranscripts((prev) => ({ ...prev, [key]: fn(prev[key] ?? EMPTY_TRANSCRIPT) }));
  }, []);

  const upsertConversations = useCallback((list: Conversation[]) => {
    if (list.length === 0) return;
    setById((prev) => {
      const next = { ...prev };
      for (const c of list) next[c.id] = c;
      return next;
    });
  }, []);

  const setActive = useCallback((target: ActiveTarget | null) => {
    activeRef.current = target;
    setActiveState(target);
    storeActive(target?.kind === 'conversation' ? target.id : null);
  }, []);

  const keyOf = (target: ActiveTarget | null): string | null =>
    target == null ? null : target.kind === 'conversation' ? convKey(target.id) : target.key;

  const clearStallTimer = useCallback((key: string) => {
    const t = stallTimersRef.current.get(key);
    if (t) clearTimeout(t);
    stallTimersRef.current.delete(key);
  }, []);

  // End a turn that will never get its reply: drop the partial text, unlock
  // the input and leave a notice so the clinician can resend.
  const endFailedTurn = useCallback(
    (key: string, notice: string) => {
      clearStallTimer(key);
      const dropped = new Set<string>();
      for (const [runId, p] of provisionalRef.current) {
        if (p.key !== key) continue;
        completedRunIdsRef.current.add(runId);
        dropped.add(p.messageId);
        provisionalRef.current.delete(runId);
      }
      awaitingRef.current.delete(key);
      updateTranscript(key, (t) => ({
        ...t,
        typing: false,
        statusLabel: null,
        messages: [
          ...t.messages.filter((m) => !dropped.has(m.id)),
          { id: makeId(), role: 'assistant', text: notice, ts: Date.now(), messageType: 'system_notice' },
        ],
      }));
    },
    [clearStallTimer, updateTranscript],
  );

  const armStallTimer = useCallback(
    (key: string) => {
      clearStallTimer(key);
      stallTimersRef.current.set(
        key,
        setTimeout(() => {
          stallTimersRef.current.delete(key);
          endFailedTurn(key, RUN_FAILED_NOTICE);
        }, STREAM_STALL_TIMEOUT_MS),
      );
    },
    [clearStallTimer, endFailedTurn],
  );

  // The server answered: stop waiting for an acknowledgement of these messages.
  const acknowledge = useCallback((key: string | null, clientMessageId?: string) => {
    for (const [msgId, a] of Array.from(ackTimersRef.current)) {
      if (msgId === clientMessageId || (key != null && a.key === key)) {
        clearTimeout(a.timer);
        ackTimersRef.current.delete(msgId);
      }
    }
  }, []);

  // Forget cached transcripts beyond the most recent ones (never the one on
  // screen, a draft, or one still waiting for its reply).
  const touchTranscript = useCallback((key: string) => {
    const order = openedOrderRef.current.filter((k) => k !== key);
    order.push(key);
    openedOrderRef.current = order;
    const evictable = order.filter((k) => k.startsWith('c:') && k !== key && !awaitingRef.current.has(k));
    const excess = evictable.length - TRANSCRIPT_CACHE;
    if (excess <= 0) return;
    const evict = new Set(evictable.slice(0, excess));
    openedOrderRef.current = order.filter((k) => !evict.has(k));
    setTranscripts((prev) => {
      const next = { ...prev };
      for (const k of evict) delete next[k];
      return next;
    });
  }, []);

  // ------------------------------------------------------------- transcript

  const loadTranscript = useCallback(
    async (id: number, { force = false } = {}) => {
      const key = convKey(id);
      const current = transcriptsRef.current[key];
      if (!force && current && (current.loaded || current.loading)) return;
      updateTranscript(key, (t) => ({ ...t, loading: true, error: false }));
      try {
        const page = await agentConversationsService.getMessages(id, { limit: FIRST_MESSAGES });
        updateTranscript(key, (t) => ({
          ...t,
          // The server's page includes everything it saved, live bubbles of
          // this session too: only a message that never reached it survives.
          messages: [...page.messages, ...t.messages.filter((m) => m.sendState === 'failed')],
          loaded: true,
          loading: false,
          hasMore: page.hasMore,
          nextBeforeId: page.nextBeforeId,
        }));
      } catch (err) {
        console.warn('AgentConversations: failed to load messages', err);
        updateTranscript(key, (t) => ({ ...t, loading: false, error: true }));
      }
    },
    [updateTranscript],
  );

  const loadOlderMessages = useCallback(async () => {
    const target = activeRef.current;
    if (target?.kind !== 'conversation') return;
    const key = convKey(target.id);
    const t = transcriptsRef.current[key];
    if (!t || !t.hasMore || t.loadingMore || t.nextBeforeId == null) return;
    updateTranscript(key, (x) => ({ ...x, loadingMore: true }));
    try {
      const page = await agentConversationsService.getMessages(target.id, {
        beforeId: t.nextBeforeId,
        limit: OLDER_MESSAGES,
      });
      updateTranscript(key, (x) => ({
        ...x,
        messages: [...page.messages, ...x.messages],
        hasMore: page.hasMore,
        nextBeforeId: page.nextBeforeId,
        loadingMore: false,
      }));
    } catch (err) {
      console.warn('AgentConversations: failed to load older messages', err);
      updateTranscript(key, (x) => ({ ...x, loadingMore: false }));
    }
  }, [updateTranscript]);

  // ------------------------------------------------------------- navigation

  const openConversation = useCallback(
    (id: number) => {
      setActive({ kind: 'conversation', id });
      setView('thread');
      setUnread((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      touchTranscript(convKey(id));
      loadTranscript(id);
    },
    [loadTranscript, setActive, touchTranscript],
  );

  const startDraft = useCallback(
    (patient: DraftPatient | null = null) => {
      const key = makeId('draft');
      setTranscripts((prev) => ({ ...prev, [key]: { ...EMPTY_TRANSCRIPT, loaded: true } }));
      setActive({ kind: 'draft', key, patient });
      setView('thread');
    },
    [setActive],
  );

  const setDraftPatient = useCallback(
    (patient: DraftPatient | null) => {
      const target = activeRef.current;
      if (target?.kind !== 'draft') return;
      if (patient) patientNameCacheRef.current.set(patient.id, patient.name);
      setActive({ ...target, patient });
    },
    [setActive],
  );

  const showList = useCallback(() => setView('list'), []);
  const togglePin = useCallback(() => setPinned((p) => !p), []);

  const patientName = useCallback(async (id: number): Promise<string> => {
    const cached = patientNameCacheRef.current.get(id);
    if (cached) return cached;
    try {
      const p = await patientsService.getPatientById(id);
      const name = `${p.firstName} ${p.lastName}`.trim() || p.username || `Paciente #${id}`;
      patientNameCacheRef.current.set(id, name);
      return name;
    } catch {
      return `Paciente #${id}`;
    }
  }, []);

  // Show this patient's newest conversation, or a draft bound to them.
  const followPatient = useCallback(
    async (patientId: number, knownName?: string) => {
      const seq = ++routeSeqRef.current;
      if (knownName) patientNameCacheRef.current.set(patientId, knownName);
      let newest: Conversation | null = null;
      try {
        newest = await agentConversationsService.newestForPatient(patientId);
      } catch (err) {
        console.warn('AgentConversations: failed to find the patient conversation', err);
      }
      if (routeSeqRef.current !== seq) return;
      if (newest) {
        upsertConversations([newest]);
        openConversation(newest.id);
        return;
      }
      const name = await patientName(patientId);
      if (routeSeqRef.current !== seq) return;
      startDraft({ id: patientId, name });
    },
    [openConversation, patientName, startDraft, upsertConversations],
  );

  const openForPatient = useCallback(
    async (patient: DraftPatient) => {
      setPinned(false);
      const target = activeRef.current;
      const current =
        target?.kind === 'conversation'
          ? byIdRef.current[target.id]?.patient?.id
          : target?.kind === 'draft'
            ? target.patient?.id
            : undefined;
      if (current === patient.id && !(target?.kind === 'conversation' && byIdRef.current[target.id]?.archived)) {
        setView('thread');
        return;
      }
      await followPatient(patient.id, patient.name);
    },
    [followPatient],
  );

  // Loads the group of the open patient's conversations shown first in the list.
  const loadRouteGroup = useCallback(
    async (patientId: number) => {
      try {
        const page = await agentConversationsService.list({ patientId, perpage: PAGE_SIZE });
        if (routePatientRef.current?.id !== patientId) return;
        upsertConversations(page.conversations);
        setRouteIds(page.conversations.map((c) => c.id));
      } catch (err) {
        console.warn('AgentConversations: failed to load the patient conversations', err);
      }
    },
    [upsertConversations],
  );

  const syncRoutePatient = useCallback(
    (patientId?: number) => {
      if (patientId == null) {
        // Leaving patient pages keeps the conversation on screen.
        routePatientRef.current = null;
        setRoutePatient(null);
        setRouteIds([]);
        return;
      }
      if (routePatientRef.current?.id === patientId) return;
      const placeholder = { id: patientId, name: patientNameCacheRef.current.get(patientId) ?? '' };
      routePatientRef.current = placeholder;
      setRoutePatient(placeholder);
      setRouteIds([]);
      loadRouteGroup(patientId);
      patientName(patientId).then((name) => {
        if (routePatientRef.current?.id === patientId) {
          routePatientRef.current = { id: patientId, name };
          setRoutePatient({ id: patientId, name });
        }
      });
      if (pinnedRef.current) return;
      const target = activeRef.current;
      const onScreen =
        target?.kind === 'conversation'
          ? byIdRef.current[target.id]?.patient?.id
          : target?.kind === 'draft'
            ? target.patient?.id
            : undefined;
      if (onScreen === patientId) return;
      followPatient(patientId);
    },
    [followPatient, loadRouteGroup, patientName],
  );

  // ------------------------------------------------------------------- list

  const fetchList = useCallback(
    async (page: number) => {
      const seq = ++listSeqRef.current;
      setListLoading(true);
      setListError(false);
      try {
        const res = await agentConversationsService.list({
          filter: listFilterRef.current,
          q: listQueryRef.current,
          page,
          perpage: PAGE_SIZE,
        });
        if (listSeqRef.current !== seq) return;
        upsertConversations(res.conversations);
        const ids = res.conversations.map((c) => c.id);
        // A conversation can move up between two pages: keep the first sighting.
        setListIds((prev) => (page === 1 ? ids : [...prev, ...ids.filter((id) => !prev.includes(id))]));
        setListPage(res.page);
        setListTotalPages(res.totalPages);
      } catch (err) {
        console.warn('AgentConversations: failed to load conversations', err);
        if (listSeqRef.current === seq) setListError(true);
      } finally {
        if (listSeqRef.current === seq) setListLoading(false);
      }
    },
    [upsertConversations],
  );
  const setListFilter = useCallback((f: ConversationListFilter) => {
    listFilterRef.current = f;
    setListFilterState(f);
  }, []);
  const setListQuery = useCallback((q: string) => {
    listQueryRef.current = q;
    setListQueryState(q);
  }, []);

  useEffect(() => {
    if (!access) return;
    fetchList(1);
  }, [access, listFilter, listQuery, fetchList]);

  const reloadConversations = useCallback(() => {
    fetchList(1);
  }, [fetchList]);

  const loadMoreConversations = useCallback(async () => {
    if (listLoading || listPage >= listTotalPages) return;
    await fetchList(listPage + 1);
  }, [fetchList, listLoading, listPage, listTotalPages]);

  // A created or updated conversation: refresh it and place it in the lists
  // it belongs to, newest first.
  const placeConversation = useCallback(
    (c: Conversation) => {
      upsertConversations([c]);
      const known = { ...byIdRef.current, [c.id]: c };
      const resort = (ids: number[]) =>
        sortConversations(ids.map((id) => known[id]).filter(Boolean)).map((x) => x.id);
      setListIds((prev) => {
        const inList = prev.includes(c.id);
        const belongs = matchesFilter(c, listFilterRef.current) && !listQueryRef.current.trim();
        if (belongs) return resort(inList ? prev : [c.id, ...prev]);
        if (inList && !matchesFilter(c, listFilterRef.current)) return prev.filter((id) => id !== c.id);
        return inList ? resort(prev) : prev;
      });
      if (c.patient && c.patient.id === routePatientRef.current?.id) {
        setRouteIds((prev) =>
          c.archived ? prev.filter((id) => id !== c.id) : resort(prev.includes(c.id) ? prev : [c.id, ...prev]),
        );
      }
    },
    [upsertConversations],
  );

  const dropConversation = useCallback((id: number) => {
    setListIds((prev) => prev.filter((x) => x !== id));
    setRouteIds((prev) => prev.filter((x) => x !== id));
    setTranscripts((prev) => {
      const next = { ...prev };
      delete next[convKey(id)];
      return next;
    });
    const target = activeRef.current;
    if (target?.kind === 'conversation' && target.id === id) {
      setActive(null);
      setView('list');
      return true;
    }
    return false;
  }, [setActive]);

  // --------------------------------------------------------------- restore

  useEffect(() => {
    if (!access) return;
    const stored = readStoredActive();
    if (stored == null || activeRef.current) return;
    agentConversationsService
      .get(stored)
      .then((c) => {
        if (activeRef.current) return; // the route already chose one
        upsertConversations([c]);
        openConversation(c.id);
      })
      .catch(() => storeActive(null));
  }, [access, openConversation, upsertConversations]);

  // -------------------------------------------------------------- proposals

  const setProposalStatus = useCallback((proposalId: string, patch: Partial<ProposalState>) => {
    setTranscripts((prev) => {
      let changed = false;
      const next: Record<string, Transcript> = {};
      for (const [k, t] of Object.entries(prev)) {
        const messages = patchProposals(t.messages, proposalId, patch);
        if (messages !== t.messages) changed = true;
        next[k] = messages === t.messages ? t : { ...t, messages };
      }
      return changed ? next : prev;
    });
  }, []);

  // Context proposals change the patient's context: tell open context views.
  const noteContextChange = useCallback((r: ProposalResolveResult) => {
    if (r.ok && r.action === 'update_patient_context' && r.patient_id != null && terminalStatus(r.status) === 'applied') {
      emitPatientContextChanged(r.patient_id);
    }
  }, []);

  const applyResolutions = useCallback(
    (results: ProposalResolveResult[]) => {
      for (const r of results) {
        if (!r.proposal_id) continue;
        const status = terminalStatus(r.status);
        if (!status) continue;
        setProposalStatus(r.proposal_id, { status: status as ProposalStatus, error: undefined, detail: r.detail });
        noteContextChange(r);
      }
    },
    [noteContextChange, setProposalStatus],
  );

  const resolveManyProposals = useCallback(
    async (proposalIds: string[], decision: 'accept' | 'reject') => {
      const ids = Array.from(new Set(proposalIds.filter(Boolean)));
      if (ids.length === 0) return;
      ids.forEach((id) => setProposalStatus(id, { status: 'applying', error: undefined }));
      try {
        const results = await resolveProposals(ids.map((id) => ({ proposal_id: id, decision })));
        const byProposal = new Map(results.map((r) => [r.proposal_id, r]));
        ids.forEach((id) => {
          const r = byProposal.get(id);
          if (r && r.ok) {
            const status: ProposalStatus =
              terminalStatus(r.status) ?? (decision === 'accept' ? 'applied' : 'discarded');
            setProposalStatus(id, { status, error: undefined, detail: r.detail });
            noteContextChange({ ...r, status });
          } else {
            setProposalStatus(id, { status: 'error', error: proposalErrorMessage(r?.error) });
          }
        });
      } catch (err: any) {
        const message =
          err?.response?.detail ||
          (err?.response && typeof err.response === 'object' ? Object.values(err.response).flat().join(' ') : null) ||
          err?.message;
        ids.forEach((id) => setProposalStatus(id, { status: 'error', error: proposalErrorMessage(null, message) }));
      }
    },
    [noteContextChange, setProposalStatus],
  );

  const resolveProposal = useCallback(
    (proposalId: string, decision: 'accept' | 'reject') => resolveManyProposals([proposalId], decision),
    [resolveManyProposals],
  );

  // ----------------------------------------------------------------- socket

  // Which transcript a frame belongs to. Turn frames always carry a
  // conversation; a draft's first frame is conversation_created.
  // Frames only fill transcripts that are loaded or waiting for our own reply;
  // any other conversation just gets its unread mark, and loads in full when
  // opened (otherwise the live reply would show twice next to the saved one).
  const frameKey = (conversationId: number | null | undefined): string | null => {
    if (conversationId == null) return null;
    const key = convKey(conversationId);
    return transcriptsRef.current[key]?.loaded || awaitingRef.current.has(key) ? key : null;
  };

  const markUnread = useCallback((id: number) => {
    const target = activeRef.current;
    const onScreen =
      panelOpenRef.current && viewRef.current === 'thread' && target?.kind === 'conversation' && target.id === id;
    if (onScreen) return;
    setUnread((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);

  useEffect(() => {
    if (!access) {
      socketRef.current?.close();
      socketRef.current = null;
      setConnectionState('closed');
      return;
    }
    if (socketRef.current) return;
    setAuthError(false);

    const socket = new ClinicAgentSocket(() => localStorage.getItem('access') || undefined, {
      onStateChange: (s) => {
        setConnectionState(s);
        if (s === 'closed' || s === 'reconnecting') {
          wasDisconnectedRef.current = true;
          // A dropped connection loses every turn in flight server-side.
          for (const key of Array.from(awaitingRef.current)) endFailedTurn(key, CONNECTION_LOST_NOTICE);
          for (const key of Array.from(stallTimersRef.current.keys())) clearStallTimer(key);
          for (const a of ackTimersRef.current.values()) clearTimeout(a.timer);
          ackTimersRef.current.clear();
          provisionalRef.current.clear();
        }
        if (s === 'open' && wasDisconnectedRef.current) {
          // Frames sent while disconnected are lost: refetch what is on screen
          // and let the other cached transcripts reload when opened.
          wasDisconnectedRef.current = false;
          fetchList(1);
          setTranscripts((prev) => {
            const next: Record<string, Transcript> = {};
            for (const [k, t] of Object.entries(prev)) next[k] = k.startsWith('c:') ? { ...t, loaded: false } : t;
            return next;
          });
          const target = activeRef.current;
          if (target?.kind === 'conversation') loadTranscript(target.id, { force: true });
        }
      },
      onTyping: ({ conversation_id, client_message_id }) => {
        const key =
          frameKey(conversation_id) ??
          (client_message_id ? draftSendsRef.current.get(client_message_id) ?? null : null);
        acknowledge(key, client_message_id);
        if (!key) return;
        awaitingRef.current.add(key);
        updateTranscript(key, (t) => ({ ...t, typing: true }));
      },
      onAgentStatus: ({ conversation_id, label, run_id }) => {
        const key = frameKey(conversation_id);
        acknowledge(key);
        if (!key) return;
        if (run_id !== null && completedRunIdsRef.current.has(run_id)) return;
        awaitingRef.current.add(key);
        updateTranscript(key, (t) => ({ ...t, statusLabel: label }));
        if (run_id !== null && provisionalRef.current.has(run_id)) armStallTimer(key);
      },
      onAssistantDelta: ({ conversation_id, run_id, call, text }) => {
        const key = frameKey(conversation_id);
        acknowledge(key);
        if (!key || !Number.isFinite(run_id) || completedRunIdsRef.current.has(run_id)) return;
        awaitingRef.current.add(key);
        armStallTimer(key);
        const current = provisionalRef.current.get(run_id);
        if (!current) {
          const id = makeId();
          provisionalRef.current.set(run_id, { key, messageId: id, call });
          updateTranscript(key, (t) => ({
            ...t,
            statusLabel: null,
            messages: [...t.messages, { id, role: 'assistant', text, ts: Date.now(), runId: run_id, streaming: true }],
          }));
          return;
        }
        // A new model call starts the reply over; chunks never concatenate across calls.
        const restart = current.call !== call;
        current.call = call;
        updateTranscript(key, (t) => ({
          ...t,
          statusLabel: null,
          messages: t.messages.map((m) =>
            m.id === current.messageId ? { ...m, text: restart ? text : m.text + text } : m,
          ),
        }));
      },
      onAssistantMessage: ({ conversation_id, text, proposals, run_id }) => {
        if (conversation_id != null) markUnread(conversation_id);
        const key = frameKey(conversation_id);
        acknowledge(key);
        if (!key) return;
        if (Number.isFinite(run_id)) completedRunIdsRef.current.add(run_id);
        const provisional = provisionalRef.current.get(run_id);
        provisionalRef.current.delete(run_id);
        clearStallTimer(key);
        awaitingRef.current.delete(key);
        const proposalStates: ProposalState[] = (proposals || []).map((p: Proposal) => ({
          ...p,
          run_id: p.run_id ?? (Number.isFinite(run_id) ? run_id : undefined),
          status: 'pending',
        }));
        updateTranscript(key, (t) => {
          const base = { ...t, typing: false, statusLabel: null };
          if (text.trim() === '' && proposalStates.length === 0) {
            return provisional ? { ...base, messages: t.messages.filter((m) => m.id !== provisional.messageId) } : base;
          }
          const reply: ChatMessage = {
            id: provisional?.messageId ?? makeId(),
            role: 'assistant',
            text,
            proposals: proposalStates.length > 0 ? proposalStates : undefined,
            ts: Date.now(),
            runId: Number.isFinite(run_id) ? run_id : undefined,
          };
          const replaced = provisional && t.messages.some((m) => m.id === provisional.messageId);
          return {
            ...base,
            messages: replaced
              ? t.messages.map((m) => (m.id === provisional!.messageId ? reply : m))
              : [...t.messages, reply],
          };
        });
      },
      onRunError: ({ conversation_id, client_message_id, code, message }) => {
        const draftKey = client_message_id ? draftSendsRef.current.get(client_message_id) : undefined;
        const key = frameKey(conversation_id) ?? draftKey ?? null;
        acknowledge(key, client_message_id);
        if (!key) return;
        // The message never started a turn: mark it failed so it can be resent.
        if (client_message_id && code in SEND_ERROR_TEXT) {
          awaitingRef.current.delete(key);
          draftSendsRef.current.delete(client_message_id);
          const sent = sentPayloadsRef.current.get(client_message_id);
          if (sent) failedPayloadsRef.current.set(client_message_id, sent);
          updateTranscript(key, (t) => ({
            ...t,
            typing: false,
            statusLabel: null,
            messages: [
              ...t.messages.map((m) => (m.id === client_message_id ? { ...m, sendState: 'failed' as const } : m)),
              { id: makeId(), role: 'assistant', text: SEND_ERROR_TEXT[code], ts: Date.now(), messageType: 'system_notice' },
            ],
          }));
          return;
        }
        endFailedTurn(key, message.trim() || RUN_FAILED_NOTICE);
      },
      onUserTranscript: ({ conversation_id, client_message_id, chat_id, text }) => {
        // The voice bubble was appended with an empty text; this is what the
        // assistant understood. The bubble is matched by client_message_id
        // (a draft send is rekeyed onto the conversation on conversation_created).
        // Like typing/error frames, it may precede conversation_created on a
        // draft's first message, so fall back to the draft the id belongs to.
        const key =
          frameKey(conversation_id) ??
          (client_message_id ? draftSendsRef.current.get(client_message_id) ?? null : null);
        if (!key) return;
        updateTranscript(key, (t) => {
          // Without an id (the contract always echoes ours; defensive), the
          // latest voice bubble still waiting for its transcript takes it.
          const targetId =
            client_message_id ??
            [...t.messages].reverse().find((m) => m.role === 'user' && isAudioFileType(m.fileType) && m.text === '')?.id;
          if (!targetId) return t;
          return {
            ...t,
            messages: t.messages.map((m) => (m.id === targetId ? { ...m, text, serverId: chat_id } : m)),
          };
        });
      },
      onProactiveMessage: () => {
        // The clinic agent never pushes; ignore defensively.
      },
      onProposalResolved: ({ results }) => applyResolutions(results),
      onProposalAck: () => {},
      onConversationCreated: ({ conversation, client_message_id }) => {
        const draftKey = client_message_id ? draftSendsRef.current.get(client_message_id) : undefined;
        if (client_message_id) {
          draftSendsRef.current.delete(client_message_id);
          acknowledge(null, client_message_id);
        }
        placeConversation(conversation);
        if (!draftKey) return; // created from another tab
        const key = convKey(conversation.id);
        // The draft becomes the conversation: same bubbles, now addressable by id.
        setTranscripts((prev) => {
          const draft = prev[draftKey];
          const next = { ...prev };
          delete next[draftKey];
          next[key] = { ...(draft ?? EMPTY_TRANSCRIPT), loaded: true };
          return next;
        });
        for (const [msgId, f] of failedPayloadsRef.current) {
          if (f.key === draftKey) failedPayloadsRef.current.set(msgId, { key, payload: { ...f.payload, conversation_id: conversation.id } });
        }
        if (awaitingRef.current.delete(draftKey)) awaitingRef.current.add(key);
        const target = activeRef.current;
        if (target?.kind === 'draft' && target.key === draftKey) {
          setActive({ kind: 'conversation', id: conversation.id });
          touchTranscript(key);
        }
      },
      onConversationUpdated: ({ conversation }) => placeConversation(conversation),
      onConversationDeleted: ({ conversation_id }) => {
        if (dropConversation(conversation_id)) toast('Conversación eliminada');
      },
      onUnauthorized: () => setAuthError(true),
      refreshToken: async () => {
        try {
          const result = await refreshTokensRef.current();
          return !!result?.success;
        } catch {
          return false;
        }
      },
    });
    socketRef.current = socket;
    socket.connect();
    return () => {
      for (const key of Array.from(stallTimersRef.current.keys())) clearStallTimer(key);
      for (const a of ackTimersRef.current.values()) clearTimeout(a.timer);
      socket.close();
      if (socketRef.current === socket) socketRef.current = null;
    };
    // The handlers read refs; the socket is built once per session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access]);

  // Background tabs drop idle sockets: reconnect when the tab comes back.
  useEffect(() => {
    if (!access) return;
    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      const socket = socketRef.current;
      if (!socket || socket.isOpen) return;
      try {
        await refreshTokensRef.current();
      } catch {
        /* reconnect anyway */
      }
      socket.forceReconnect();
    };
    const onOnline = () => socketRef.current?.forceReconnect();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [access]);

  // ------------------------------------------------------------------- send

  // Ends a turn whose message the server never acknowledged, and offers the
  // message for retry. The key is re-read on expiry: a draft may have become
  // a conversation in between (then it was acknowledged and this never runs).
  const armAckTimer = useCallback(
    (key: string, messageId: string) => {
      const timer = setTimeout(() => {
        const entry = ackTimersRef.current.get(messageId);
        ackTimersRef.current.delete(messageId);
        if (!entry) return;
        const k = entry.key;
        awaitingRef.current.delete(k);
        const clientId = sentPayloadsRef.current.get(messageId)?.payload.client_message_id;
        if (clientId) draftSendsRef.current.delete(clientId);
        const sent = sentPayloadsRef.current.get(messageId);
        if (sent) failedPayloadsRef.current.set(messageId, sent);
        updateTranscript(k, (t) => ({
          ...t,
          typing: false,
          statusLabel: null,
          messages: [
            ...t.messages.map((m) => (m.id === messageId ? { ...m, sendState: 'failed' as const } : m)),
            { id: makeId(), role: 'assistant', text: NO_ACK_NOTICE, ts: Date.now(), messageType: 'system_notice' },
          ],
        }));
      }, SEND_ACK_TIMEOUT_MS);
      ackTimersRef.current.set(messageId, { key, timer });
    },
    [updateTranscript],
  );

  const pushUserPayload = useCallback(
    (key: string, messageId: string, payload: ClinicMessagePayload) => {
      // Everything a reply needs is in place before sending, so frames find
      // the draft and the turn however soon they arrive.
      const draftClientId =
        payload.conversation_id === null && payload.client_message_id ? payload.client_message_id : null;
      if (draftClientId) draftSendsRef.current.set(draftClientId, key);
      const wasAwaiting = awaitingRef.current.has(key);
      awaitingRef.current.add(key);
      sentPayloadsRef.current.set(messageId, { key, payload });
      failedPayloadsRef.current.delete(messageId);
      updateTranscript(key, (t) => ({
        ...t,
        typing: true,
        messages: t.messages.map((m) => (m.id === messageId && m.sendState ? { ...m, sendState: undefined } : m)),
      }));

      const ok = socketRef.current ? socketRef.current.send(payload) : false;
      if (ok) {
        armAckTimer(key, messageId);
        return;
      }
      // Never left the client (socket closed, or the frame failed to serialize).
      if (draftClientId) draftSendsRef.current.delete(draftClientId);
      if (!wasAwaiting) awaitingRef.current.delete(key);
      sentPayloadsRef.current.delete(messageId);
      failedPayloadsRef.current.set(messageId, { key, payload });
      updateTranscript(key, (t) => ({
        ...t,
        typing: wasAwaiting && t.typing,
        messages: t.messages.map((m) => (m.id === messageId ? { ...m, sendState: 'failed' } : m)),
      }));
    },
    [armAckTimer, updateTranscript],
  );

  const sendMessage = useCallback(
    (text: string, file?: { dataUrl: string; type: string; name: string; durationMillis?: number }) => {
      const trimmed = text.trim();
      if (!trimmed && !file) return;
      const target = activeRef.current;
      const key = keyOf(target);
      if (!target || !key) return;
      const isPdf = file?.type === 'application/pdf';
      const id = makeId();
      updateTranscript(key, (t) => ({
        ...t,
        messages: [
          ...t.messages,
          {
            id,
            role: 'user',
            text: trimmed,
            ts: Date.now(),
            ...(file ? { fileUrl: file.dataUrl, fileType: file.type } : {}),
            ...(isPdf ? { fileName: file!.name } : {}),
            ...(file?.durationMillis != null ? { durationMillis: file.durationMillis } : {}),
          },
        ],
      }));
      const common = {
        type: 'message' as const,
        client_message_id: id,
        text: trimmed,
        ...(file ? { file_data: file.dataUrl, file_type: file.type } : {}),
        ...(isPdf ? { file_name: file!.name } : {}),
      };
      // patient_id only binds a new conversation; an existing one already has its patient.
      const payload: ClinicMessagePayload =
        target.kind === 'conversation'
          ? { ...common, conversation_id: target.id }
          : { ...common, conversation_id: null, ...(target.patient ? { patient_id: target.patient.id } : {}) };
      pushUserPayload(key, id, payload);
    },
    [pushUserPayload, updateTranscript],
  );

  const retrySend = useCallback(
    (messageId: string) => {
      const failed = failedPayloadsRef.current.get(messageId);
      if (!failed) return;
      pushUserPayload(failed.key, messageId, failed.payload);
    },
    [pushUserPayload],
  );

  // ----------------------------------------------------------------- manage

  const rename = useCallback(
    async (id: number, title: string) => {
      const updated = await agentConversationsService.update(id, { title });
      placeConversation(updated);
    },
    [placeConversation],
  );

  const setArchived = useCallback(
    async (id: number, archived: boolean) => {
      const updated = await agentConversationsService.update(id, { archived });
      placeConversation(updated);
      const target = activeRef.current;
      if (archived && target?.kind === 'conversation' && target.id === id) setView('list');
      return updated;
    },
    [placeConversation],
  );

  const remove = useCallback(
    async (id: number) => {
      await agentConversationsService.remove(id);
      dropConversation(id);
    },
    [dropConversation],
  );

  // ------------------------------------------------------------------ value

  const conversations = useMemo(
    () => listIds.map((id) => byId[id]).filter((c): c is Conversation => Boolean(c)),
    [byId, listIds],
  );
  const routePatientConversations = useMemo(
    () => routeIds.map((id) => byId[id]).filter((c): c is Conversation => Boolean(c) && !c.archived),
    [byId, routeIds],
  );
  const activeKey = keyOf(active);
  const activeTranscript = (activeKey && transcripts[activeKey]) || EMPTY_TRANSCRIPT;
  const activeConversation = active?.kind === 'conversation' ? byId[active.id] ?? null : null;
  const activeBusy = activeTranscript.typing || activeTranscript.statusLabel != null;

  const value = useMemo<AgentConversationsValue>(
    () => ({
      connectionState,
      authError,
      conversations,
      listFilter,
      listQuery,
      listLoading,
      listError,
      hasMoreConversations: listPage < listTotalPages,
      setListFilter,
      setListQuery,
      loadMoreConversations,
      reloadConversations,
      routePatient,
      routePatientConversations,
      unread,
      view,
      active,
      activeConversation,
      activeTranscript,
      activeBusy,
      pinned,
      togglePin,
      showList,
      openConversation,
      startDraft,
      setDraftPatient,
      openForPatient,
      syncRoutePatient,
      sendMessage,
      retrySend,
      loadOlderMessages,
      rename,
      setArchived,
      remove,
      resolveProposal,
      resolveManyProposals,
    }),
    [
      connectionState, authError, conversations, listFilter, listQuery, listLoading, listError, listPage,
      listTotalPages, setListFilter, setListQuery, loadMoreConversations, reloadConversations, routePatient, routePatientConversations,
      unread, view, active, activeConversation, activeTranscript, activeBusy, pinned, togglePin, showList,
      openConversation, startDraft, setDraftPatient, openForPatient, syncRoutePatient, sendMessage, retrySend,
      loadOlderMessages, rename, setArchived, remove, resolveProposal, resolveManyProposals,
    ],
  );

  return (
    <AgentConversationsContext.Provider value={enabled ? value : undefined}>{children}</AgentConversationsContext.Provider>
  );
};

export const useAgentConversations = (): AgentConversationsValue | null =>
  useContext(AgentConversationsContext) ?? null;
